// HelpTab — pen Q68 "Help: the tour and feedback in one place", pure.
//
// `HelpTab` peeks up from the page's bottom edge, like the handle of a
// drawer: a raised surface that lies on top of the page, open at the bottom.
// Open (its menu or the feedback panel above it), it looks pressed and the
// chevron points down. In the Studio it is instead a quiet row at the foot
// of the outline (`look="row"`, pen Q76), so it never covers the page. `HelpMenu` is what it opens: "Take a guided tour" of
// the view on screen (only when that view has a tour; New until taken), and
// "Give feedback" (Beta), which gives way to the feedback panel.
//
// Props-driven: every pick reports up; the popover around them is the host's
// (FeedbackHost).

export { HelpMenu, HelpTab };

import { useLingui } from "@lingui/react/macro";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import type { ViewTour } from "../GuidedTour/viewTours.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import "./HelpTab.css";

type HelpTabProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly open: boolean;
    readonly look: "tab" | "row";
};

// The trigger of the host's popover, so it takes the ref and props Radix
// hands a trigger.
const HelpTab = forwardRef<HTMLButtonElement, HelpTabProps>(
    ({ open, look, ...props }, ref) => {
        const { t } = useLingui();
        return (
            <button
                ref={ref}
                type="button"
                className={`bilda-help-tab bilda-help-tab--${look}`}
                {...props}
            >
                <Icon name="help" size={look === "tab" ? "sm" : "xs"} />
                <span>{t`Help and feedback`}</span>
                {look === "tab" ? (
                    <Icon
                        name={open ? "chevron-down" : "chevron-up"}
                        size="xs"
                    />
                ) : null}
            </button>
        );
    },
);

type HelpRowProps = {
    readonly icon: IconName;
    readonly label: string;
    readonly badge: ReactNode;
    readonly hint: string;
    readonly onSelect: () => void;
};

const HelpRow = ({
    icon,
    label,
    badge,
    hint,
    onSelect,
}: HelpRowProps): ReactNode => (
    <button type="button" className="bilda-help-menu__row" onClick={onSelect}>
        <Icon name={icon} size="sm" />
        <span className="bilda-help-menu__text">
            <span className="bilda-help-menu__label">
                {label}
                {badge}
            </span>
            <span className="bilda-help-menu__hint">{hint}</span>
        </span>
    </button>
);

type HelpMenuProps = {
    // The view on screen's tour; null when it has none (only Give feedback).
    readonly tour: ViewTour | null;
    readonly onTour: () => void;
    readonly onFeedback: () => void;
};

const HelpMenu = ({ tour, onTour, onFeedback }: HelpMenuProps): ReactNode => {
    const { t } = useLingui();
    const view = tour?.name ?? "";
    return (
        <div className="bilda-help-menu">
            {tour === null ? null : (
                <HelpRow
                    icon="play"
                    label={t`Take a guided tour`}
                    badge={
                        tour.isNew ? (
                            <span className="bilda-help-badge bilda-help-badge--new">{t`New`}</span>
                        ) : null
                    }
                    hint={t`A short tour of ${view}`}
                    onSelect={onTour}
                />
            )}
            <HelpRow
                icon="give-feedback"
                label={t`Give feedback`}
                badge={
                    <span className="bilda-help-badge bilda-help-badge--beta">{t`Beta`}</span>
                }
                hint={t`A problem, an idea or a question`}
                onSelect={onFeedback}
            />
        </div>
    );
};
